import api from './api';
import axios from 'axios';

// Campos de metadata que viajan al presign.
// ⚠️ Antes faltaban city y category: se construían en el Modal y se perdían aquí.
const METADATA_FIELDS = [
  'projectId',
  'eventName',
  'artist',
  'date',
  'city',
  'category'
];

// --- SUBIDA DE UN ARCHIVO ---
export const uploadFileToS3 = async (file, metadata, onProgress) => {
  try {
    const payload = {
      filename: file.name,   // 👈 el nombre original viaja tal cual; la Lambda decide la key
      contentType: file.type || 'application/octet-stream',
    };

    if (metadata && typeof metadata === 'object') {
      METADATA_FIELDS.forEach((field) => {
        if (metadata[field] !== undefined && metadata[field] !== null) {
          payload[field] = metadata[field];
        }
      });
    } else if (typeof metadata === 'string') {
      payload.projectId = metadata;
    }

    const presignResponse = await api.post('/files/presign-upload', payload);
    const { uploadUrl, requiredHeaders, key } = presignResponse.data;

    await axios.put(uploadUrl, file, {
      headers: requiredHeaders || { 'Content-Type': file.type },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });

    // Devolvemos la key para poder mostrar/registrar dónde aterrizó
    return { ok: true, filename: file.name, key: key || null };
  } catch (error) {
    console.error("❌ Error en uploadFileToS3:", error);
    throw error;
  }
};

// --- SUBIDA DE VARIOS ARCHIVOS ---
/**
 * Secuencial a propósito: en paralelo se satura la subida del usuario con
 * archivos pesados y el progreso deja de ser interpretable.
 * Un archivo que falla no cancela los demás; se reporta en el resultado.
 *
 * onProgress(percent, { filename, index, total })
 */
export const uploadFilesToS3 = async (files, metadata, onProgress) => {
  const list = Array.from(files || []);
  const results = [];

  for (let index = 0; index < list.length; index++) {
    const file = list[index];
    try {
      const result = await uploadFileToS3(file, metadata, (percent) => {
        if (onProgress) {
          onProgress(percent, { filename: file.name, index, total: list.length });
        }
      });
      results.push(result);
    } catch (error) {
      results.push({ ok: false, filename: file.name, error });
    }
  }

  return results;
};

// --- LISTAR ---
export const listFiles = async (projectId = null) => {
  try {
    const config = {};
    if (projectId) {
        config.params = { projectId: projectId };
    }
    const response = await api.get('/files', config);
    return response.data.files || response.data || [];
  } catch (error) {
    console.error("❌ Error listando archivos:", error);
    return [];
  }
};

// --- MOCK CARPETAS ---
export const listFoldersInBucket = async () => [];

// --- ⬇️ DESCARGAR ---
export const downloadFile = async (file) => {
  try {
    // 🛡️ Priorizamos fullKey que viene del árbol, luego key del backend, luego name
    const fileKey = file.fullKey || file.key || file.name;

    // ⚠️ El atributo `download` se ignora en URLs cross-origin (S3 es otro origen),
    //    así que el nombre real lo tiene que imponer la Lambda con
    //    ResponseContentDisposition. Mandamos el nombre deseado para que pueda hacerlo.
    const payload = { key: fileKey, filename: file.name };

    const response = await api.post('/files/presign-download', payload);
    const downloadUrl = response.data.downloadUrl || response.data;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', file.name); // solo aplica si el backend no manda disposition
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("❌ Error descargando:", error);
    alert("No se pudo descargar el archivo. Verifica permisos.");
  }
};

// --- 🗑️ ELIMINAR ---
export const deleteFile = async (file, onRefresh) => {
  if (!window.confirm(`¿Estás seguro de eliminar ${file.name}?`)) return;

  try {
    // 🛡️ Priorizamos fullKey
    const fileKey = file.fullKey || file.key || file.name;

    const payload = { key: fileKey };

    await api.delete('/files', { data: payload });
    if (onRefresh) onRefresh();
  } catch (error) {
    console.error("❌ Error eliminando:", error);
    alert("Error al eliminar. Verifica permisos.");
  }
};
